/**
 * Runs an async worker over every item with at most `limit` calls in flight, and settles each one
 * like Promise.allSettled. A steady pool rather than fixed batches keeps a request in flight until
 * the last item finishes, so RTK Query refetches invalidated lists once at the end, not per batch.
 * @param {Array} items - The items to process
 * @param {number} limit - The maximum number of calls in flight at once
 * @param {Function} worker - Async function called with each item
 * @returns {Promise<Array<{status: string, value?: *, reason?: *}>>} One result per item, in input order
 */
export async function mapWithConcurrency(items, limit, worker) {
    const results = new Array(items.length);
    let nextIndex = 0;

    const runWorker = async () => {
        while (nextIndex < items.length) {
            const index = nextIndex++;
            try {
                results[index] = { status: "fulfilled", value: await worker(items[index]) };
            } catch (reason) {
                results[index] = { status: "rejected", reason };
            }
        }
    };

    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runWorker));
    return results;
}
